/* eslint-disable max-lines */
import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import H2 from '@base/H2/H2';
import P1 from '@base/P1/P1';
import H3 from '@base/H3/H3';
import Link from '@base/Link/Link';
import ContentsTable from '@base/ContentsTable/ContentsTable';
import MenusNav from '@base/MenusNav/MenusNav';
import { menus } from '@utils/constants';
import { Fonts, Spacings } from '@styles/index';
import Layout from '@layout/index';
import LeftContainer from '@cont/CompanyDetails/LeftContainer';
import RightContainer from '@cont/CompanyDetails/RightContainer';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  grid-column: span 24;
`;

const StyledH3 = styled(H3)`
  margin-bottom: ${Spacings.SPACING_6B};
  padding-top: 100px;
  margin-top: -100px;
`;

const Paragraph = styled(P1)`
  margin-bottom: ${Spacings.SPACING_4B};
  text-align: justify;
`;

const anchorStyle = `
  text-decoration: underline;
`;

const StyledParagraph = styled(Paragraph)`
  margin-bottom: ${Spacings.SPACING_4B};
  ol > li {
    margin-bottom: ${Spacings.SPACING_2B};
    &:first-of-type {
      margin-top: ${Spacings.SPACING_2B};
    }
  }
`;

const ListItem = ({ children }) => (
  <StyledParagraph as="li">{children}</StyledParagraph>
);

const ItemHeader = styled.span`
  font-weight: ${Fonts.SEMIBOLD};
  font-size: ${Fonts.P1};
`;

const Highlight = styled.span`
  font-weight: ${Fonts.SEMIBOLD};
`;

const Spacer = styled.div`
  height: ${Spacings.SPACING_2B};
`;

const tableData = [
  {
    label: 'A. Definitions',
    id: 'definitions'
  },
  { label: 'B. SCOPE OF APPLICATION', id: 'scope_of_application' },
  { label: 'C. Rules of Conduct', id: 'rules_of_conduct' },
  {
    label: 'D. Child Abuse and Punishments',
    id: 'child_abuse_and_punishments'
  },
  { label: 'E. Reporting', id: 'reporting' }
];

const MinorPolicy = () => {
  const router = useRouter();
  const { pathname } = router;

  return (
    <>
      <LeftContainer>
        <MenusNav data={menus} currentHref={pathname} />
      </LeftContainer>

      <RightContainer>
        <Wrapper>
          <div>
            <div>
              <H2 as="h1">
                Minor &amp; Learner Interaction - Code of Conduct Guidelines
              </H2>
            </div>
            <br />
            <br />
            <ContentsTable data={tableData} />
            <Paragraph>
              Sorting Hat Technologies Private Limited (the “
              <strong>Company</strong>”) owns and operates the website{' '}
              <Link href="/" as="/" anchorStyle={anchorStyle}>
                www.unacademy.com
              </Link>{' '}
              and mobile application (together{' '}
              <strong>“Unacademy Platform”</strong> or{' '}
              <strong>“Unacademy”</strong>). Unacademy Platform is an online
              education platform that supports and facilitates the online
              creation of educational videos/or tutorials by the users who wish
              to be educators on Unacademy Platform. Unacademy offers both live
              and recorded classes for free and via subscription. Unacademy also
              offers its iconic Learners live mentorship sessions with their
              Educators for doubt clearing, strategy &amp; study plan and
              concept clarity as part of the iconic subscription.
            </Paragraph>
            <Paragraph>
              Considering the possible interactions between the Learners (who
              may be minors i.e., under the age of 18 years) and you, the
              Company realizes the need to implement specific guidelines that
              govern your conduct on the Unacademy Platform while interacting
              with Learners (including Minors). Protecting the safety and
              well-being of children is of utmost importance to the Company and
              hence the Company has adopted this Minor Interaction - Code of
              Conduct Guidelines (
              <strong>“Minor &amp; Learner Interaction Guidelines”</strong>)
              requiring Educators/Mentors/other users to conduct themselves with
              utmost care while interacting with minors on the Unacademy
              Platform and take reasonable measures to ensure the safety of such
              minors.
            </Paragraph>
            <Paragraph>
              This Minor &amp; Learner Interaction Guidelines aims to define and
              lay down the expectations on behaviour and your actions while
              interacting with minors on Unacademy. Additionally, it will lay
              down the scope of application of this Minor &amp; Learner
              Interaction Guidelines and guidelines for reporting any issues
              relating to minors.
            </Paragraph>
            <Paragraph>
              Please read this Minor &amp; Learner Interaction Guidelines, along
              with the Privacy Policy, Terms and Conditions, User Guidelines,
              and all terms and conditions, rules and policies made available or
              published on Unacademy Platform as they shall govern your use of
              the Unacademy Platform. By using or visiting the Unacademy
              Platform or any Unacademy software, data feeds, and service
              provided to you on, from, or through the Unacademy Platform, you
              signify your agreement to (1) this Minor &amp; Learner Interaction
              Guidelines; (ii) the “Terms and Conditions”, (3) Unacademy’s
              Privacy Policy and any other terms that are updated and uploaded
              on the Unacademy Platform from time to time. If you do not agree
              to any of these terms, please do not use the Unacademy Platform.
            </Paragraph>
            <br />
            <br />
            <a name={tableData[0].id}>
              <StyledH3>{tableData[0].label}</StyledH3>
            </a>
            <ol>
              <ListItem>
                <strong>“Educators” </strong>shall mean and include all users
                who are teaching or tutoring on the Unacademy platform.
              </ListItem>
              <ListItem>
                <strong>“Learners” </strong>shall mean and include all users
                (including Minors) who have subscribed to or otherwise using
                Unacademy as a learning platform.
              </ListItem>
              <ListItem>
                <strong>“Mentors” </strong>shall mean and include all users who
                are providing the mentoring services on the Unacademy platform
                to the Learners who have subscribed to such services or
                otherwise.
              </ListItem>
              <ListItem>
                <strong>“Minor/s” or “Child/Children” </strong>shall mean and be
                construed in accordance with the Indian Majority Act, 1875, the
                Protection of Children against Sexual Offences Act, 2012 (POCSO
                Act, 2012), Child Labour (Prohibition and Regulation) Act, 1986
                and such other applicable Indian laws i.e., the term shall mean
                and include all individuals who have not attained the age of 18
                (eighteen) years.
              </ListItem>
              <ListItem>
                <strong>“Minor &amp; Learner Interaction Guidelines” </strong>
                shall mean this Minor &amp; Learner Interaction - Code of
                Conduct Guidelines, on Unacademy platform and includes all
                annexures and appendices attached thereto, if any and updates,
                changes and amendment as may be made by the Company and uploaded
                herein, from time to time.
              </ListItem>
              <ListItem>
                <strong>“You” or “Your” </strong>shall mean Educator, Learner,
                Mentors or any other Users of the Unacademy Platform.
              </ListItem>
            </ol>
            <br />
            <br />

            <a name={tableData[1].id}>
              <StyledH3>{tableData[1].label}</StyledH3>
            </a>
            <Paragraph>
              This Minor &amp; Learner Interaction Guidelines shall apply to all
              Educators, Mentors and any user on the Unacademy Platform, who
              interact with Minors on or through any feature on the Unacademy
              platform.
            </Paragraph>
            <Paragraph>
              All Educators, Mentors, Learners and any other users interacting
              with Minors, in any capacity, on or through any feature on the
              Unacademy Platform shall adhere to this Minor &amp; Learner
              Interaction Guidelines. Any violation of this Minor &amp; Learner
              Interaction Guidelines amounts to termination of your account,
              subscription or agreement with the Company, as the case may be and
              the Company also reserves the right to initiate appropriate legal
              proceedings against you if it deems necessary.
            </Paragraph>

            <br />
            <br />

            <a name={tableData[2].id}>
              <StyledH3>{tableData[2].label}</StyledH3>
            </a>
            <ol>
              <ListItem>
                <strong>No Discrimination: </strong>Treat every Minor with
                empathy and respect, regardless of his/her race, colour, gender,
                sexuality, language, religion, religious belief, heritage,
                political/other opinion, national/ethnic/social origin or
                property/disability/birth/other status; listen to them and
                respect their views.
              </ListItem>
              <ListItem>
                <strong>Conducive Learning Environment: </strong>Establish an
                atmosphere that fosters the development of Minor through your
                actions and words. Do not place a Minor at risk of harm or abuse
                and do not harm or abuse any Minor in any manner whatsoever,
                including but not limited to emotionally or sexually.
              </ListItem>
              <ListItem>
                <strong>Appropriate Communication and Behaviour: </strong>Good
                communication is the vehicle for learning and essential for
                further development of Minor. Good communication involves
                listening and talking in ways that Minors understand and feel
                heard, important and valued. Using any form of language or
                behaviour towards Minor that is inappropriate, demotivating,
                harassing, abusive, sexually provocative, demeaning,
                intimidating, offensive, discriminatory or culturally
                insensitive shall not be tolerated; further, any communication
                or behaviour while interacting with Minor shall not, in any
                manner whatsoever, induce or support physical or sexual
                relationships with Minor or portray use of or encourage the use
                of alcohol, drugs, cigarettes or other intoxicating or
                restricted substances. The Company understands that there may be
                instances when there are exchange of ideas and opinions which is
                essential in the learning process, while it is agreed that
                individuals have the right to voice their opinions, the Company
                has zero tolerance towards any form of communication or
                behaviour stated hereinabove, and the Company –
                <ol type="a">
                  <ListItem>
                    <ItemHeader>DOES NOT </ItemHeader>encourage or tolerate any
                    form of hate speech or statements that are libelous,
                    slanderous, threatening, violent, predatory, defamatory, or
                    any statement that incites hatred against specific
                    individuals or groups with respect to but not limited to
                    race or ethnic origin, country caste, religion, disability,
                    gender, age, sexual orientation/gender identity etc.; and
                  </ListItem>
                  <ListItem>
                    <ItemHeader>DOES NOT </ItemHeader>tolerate any form of
                    harassment or bullying on the Unacademy Platform and strive
                    to keep the platform a safe space to foster learning.
                    Harassment in this case would include, without limitation,
                    abusive videos, comments, messages, revealing someone’s
                    personal information, including sensitive personally
                    identifiable information of individuals, content or comments
                    uploaded in order to humiliate someone, sexual harassment or
                    sexual bullying in any form.
                  </ListItem>
                </ol>
                If it comes to the notice of the Company that your actions
                towards a Minor are inappropriate in any manner, then the
                Company reserves the right to take immediate and necessary
                actions, as it deems fit including legal proceeding, against
                you.
              </ListItem>
              <ListItem>
                <strong>Content Sharing: </strong>Your interaction with a Minor
                on Unacademy should be specific to the purpose of your
                engagement with such Minor and accordingly, information shared
                with such Minor should be strictly applicable and relatable to
                the subject-matter being discussed between you and the Minor.
                Sharing and display of inappropriate content with/to the Minor
                including but not limited to materials encouraging crime,
                violence, terrorism, racism, sexism, self-harm, suicide,
                cruelty, pornography, gambling, or any content whose sole
                objective is to sensationalize, shock or disturb individuals
                (electronically or in any other form) is not allowed and shall
                not be tolerated.
              </ListItem>
              <ListItem>
                <strong>Recording of the interactions with Minor: </strong>
                Unless recording is done by the Unacademy Platform as part of
                the Unacademy Platform offering, you shall not record or
                otherwise store the interaction and / or sessions between you
                and the Minor and/or their parents/legal guardian including
                through use of a secondary camera or any other recording
                devices.
              </ListItem>
              <ListItem>
                <strong>Impersonation: </strong>Educators/Mentors/other users
                having entered into an agreement with the Company to teach or
                otherwise use Unacademy to communicate with, the Learners
                (including Minors) are bound to fulfil their obligations in
                person (i.e., by themselves) and not assign or sublicense the
                same to another person. Educator/Mentor/such other user engaging
                any other individual/person to impersonate them or an
                Educator/Mentor/such other user impersonating as another
                Educator/Mentor/other user, while teaching or otherwise using
                Unacademy to communicate/interact with the Learners (including
                Minors) is not permitted and shall not be tolerated and
                considered as violation of this Minor &amp; Learner Interaction
                Guidelines. In this case impersonation would mean the intention
                to cause confusion regarding who the real person is by
                pretending to be them (such as using names, image, documents,
                certificates etc., not belonging to or not used to identify the
                intended Educator/Mentor/other user, or pretending to be a
                company, institute, group etc., by using their logo, brand name
                or any distinguishing mark).
              </ListItem>
              <ListItem>
                <strong>Confidentiality: </strong>Company understands that
                during the interactions between you and the Learners (including
                Minors), you may be privy to certain personal information about
                Learners (including Minors) or their parents/guardians. Hence,
                you shall at all times maintain the confidentiality of such
                information received and ensure that such information is
                dispensed to only those individuals who are legitimately
                entitled to strictly in accordance with their agreement with the
                Company and Unacademy’s terms and condition and policies
                (including its Privacy Policy) as available on Company’s
                website.
              </ListItem>
              <ListItem>
                <strong>
                  Relationship with the Learners (including Minors):{' '}
                </strong>
                The relationship between you and the Learners will be strictly
                limited to the purpose of imparting education or knowledge, or
                for such purpose as specifically stated in the agreement or
                terms of engagement communicated to you by the Company and you
                shall not form any relationship or arrangement (financial or
                otherwise) with the Learner (including Minors or their
                parents/legal guardians) which could in any way be deemed to be
                exploitative or abusive. Do not use child labour in any form.
              </ListItem>
              <ListItem>
                <strong>
                  Being Safe Online: Not everything online is trustworthy.{' '}
                </strong>
                Hence, if you are using online resources to create your teaching
                or reference materials that are to be shared with the Learners
                and/or Minors (including downloading of any content or files
                relevant to the subject being discussed with the Learners and/or
                Minors), it is important that the you assess the reliability of
                such resources and the information provided therein. Prior to
                using any such online resources or platforms it is strongly
                recommended that you exercise reasonable caution and read the
                terms and conditions for the use of such sources as provided on
                the platforms. Further, you must understand the importance of
                safeguarding your computers, laptops or any other handheld
                device used by you to access the Unacademy Platform and interact
                with the Learners (including Minors) from virus attacks, trojans
                and phishing attacks; because such attacks will not only
                threaten your privacy, but may also expose the Learners and
                their privacy to such attacks when any form
                communication/interaction takes place through such compromised
                device.
              </ListItem>
              <ListItem>
                <strong>Training Sessions: </strong> If there are any training
                sessions and workshops conducted by the Company, you must attend
                the same and ensure to implement the lessons learned from such
                sessions and workshops during your interactions with Learners.
                Further, the Company encourages you to attend training sessions
                and workshops conducted by other organizations to better
                understand the acceptable standards of conduct while interacting
                with Minors (whether online or offline), however, before
                implementing any of the lessons learnt from such sessions or
                workshops, you shall consult with the Company.
              </ListItem>
            </ol>
            <br />
            <br />

            <a name={tableData[3].id}>
              <StyledH3>{tableData[3].label}</StyledH3>
            </a>
            <Paragraph>
              Apart from the prohibited activities stated in the ‘Rules of
              Conduct’ section above, it is important for you to understand that
              Child abuse in any form (physical, emotional, or sexual) and in
              any manner (including through online platforms) could be
              punishable under the Indian Laws and you are strictly prohibited
              from engaging in such activities in any manner whatsoever.
            </Paragraph>
            <Paragraph>
              To help you better understand online Child abuse and its
              consequences, the concepts and the relative punishments under
              certain applicable laws are <strong>briefly detailed</strong>{' '}
              below for their immediate reference. However, it is strongly
              recommended that you do further research and read through all the
              applicable laws in relation to the treatment of Minors/Children,
              including the links provided herein. Further, the Company shall
              endeavour to update the Policy from time to time depending on any
              changes to the existing laws and regulations, however, it shall be
              your responsibility to keep yourself updated about the applicable
              laws and regulations from time-to-time.
            </Paragraph>
            <ol>
              <ListItem>
                <div>
                  <strong>What Constitutes Child Abuse? </strong>
                </div>
                Child abuse or maltreatment constitutes all forms of physical
                and/or emotional ill-treatment, sexual abuse, neglect or
                negligent treatment or commercial or other exploitation,
                resulting in actual or potential harm to the child’s health,
                survival, development or dignity in the context of a
                relationship of responsibility, trust or power.
                <ol type="i">
                  <ListItem>
                    <strong>Physical abuse: </strong>The intentional use of
                    physical force against a child that results in or has a high
                    likelihood of resulting in harm to the child’s health,
                    survival, development or dignity. This includes hitting,
                    beating, kicking, shaking, biting, strangling, scalding,
                    burning, poisoning and suffocating.
                  </ListItem>
                  <ListItem>
                    <strong>Emotional/mental abuse: </strong>Emotional abuse
                    includes the failure to provide a developmentally
                    appropriate, supportive environment, so that the Child can
                    develop a stable and full range of emotional and social
                    competencies proportionate with his or her personal
                    potential and in the context of the society in which the
                    Child dwells.{' '}
                    <Highlight>
                      Acts include restriction of movement, patterns of
                      belittling, denigrating, scapegoating, threatening,
                      scaring, discriminating, ridiculing or other non-physical
                      forms of hostile or rejecting treatment.
                    </Highlight>
                  </ListItem>
                  <ListItem>
                    <strong>Neglect and negligent treatment​: </strong>Neglect
                    is the failure on the part of the parents, guardian,
                    <Highlight> teacher/educators/mentors,</Highlight> or other
                    responsible party to provide for the Child’s basic needs,
                    such as food, shelter, medical care, educational
                    opportunities, or protection and supervision.
                    <Highlight>
                      {' '}
                      It is abuse through omission of the necessary environment
                      and resources for the Child to reach their full potential;
                      and the act of not recognizing physical or emotional
                      abuse.
                    </Highlight>
                  </ListItem>
                  <ListItem>
                    <strong>Exploitation: </strong>Commercial or other
                    exploitation of a Child refers to use of the Child in work
                    or other activities for the benefit of others. This
                    includes, but is not limited to, Child labour and Child
                    prostitution. These activities are to the detriment of the
                    Child’s physical or mental health, education, or spiritual,
                    moral or social-emotional development. The Child Labour
                    (prohibition and regulation) Act, 1986 and the Protection of
                    Children from Sexual Offences Act, 2012 address the issues
                    concerning Child labour and sexual abuse, and any act under
                    in relation to that is deemed a punishable offence under the
                    said regulations.
                  </ListItem>
                  <ListItem>
                    <strong>Sexual abuse​: </strong>Sexual abuse is an act or
                    behaviour which constitutes a sexual offence under the
                    Protection of Children from Sexual Offences Act, 2012
                    (‘POCSO Act, 2012’), as amended from time to time. Such
                    sexual offences include penetrative sexual assault
                    (including rape), non-penetrative sexual assault (including
                    touching the private parts of a Child), sexual harassment
                    (including stalking a Child, showing a Child pornography,
                    online grooming, and making a Child exhibit his/her body)
                    and using a Child for pornographic purposes (including
                    storing of pornographic material involving a Child for
                    commercial purposes).
                    <Spacer />
                    An abetment of and even an attempt to commit a sexual
                    offence under the POCSO Act, 2012 are also considered to be
                    offences. Certain offences under the POCSO, 2012 are
                    considered to be aggravated offences and are subject to
                    stringent punishments.
                  </ListItem>
                </ol>
              </ListItem>
              <ListItem>
                <div>
                  <strong>Legal Implications of Online Child Abuse:</strong>
                </div>
                This Minor and Learner Interaction Guidelines has only
                highlighted a few circumstances of Child Abuse and the legal
                implications thereto with an intention to give you a brief
                insight on the probable consequences of mis-utilizing the
                Unacademy Platform. Hence, you must not consider the information
                provided in this Minor and Learner Interaction Guidelines as the
                ultimate source of information relating to Child Abuse and its
                legal implications. It is strongly recommended that they keep
                themselves updated about the applicable laws and regulations
                from time-to-time.
                <ol type="a">
                  <ListItem>
                    <strong>
                      Juvenile Justice (Care and Protection of Children) Act,
                      2015:{' '}
                    </strong>
                    It is to be noted that the Juvenile Justice (Care and
                    Protection of Children) Act, 2015 criminalizes acts that may
                    cause a child mental or physical suffering. As per section
                    75 of the Act, whoever, having the actual charge of, or
                    control over, a Child, assaults, abandons, abuses, exposes
                    or willfully neglects the Child or causes or procures the
                    Child to be assaulted, abandoned, abused, exposed or
                    neglected in a manner likely to cause such Child unnecessary
                    mental or physical suffering, shall be punishable with
                    imprisonment for a term which may extend to three years or
                    with fine of one lakh rupees or with both.
                  </ListItem>
                  <ListItem>
                    <strong>Sexual Harassment: </strong>POCSO Act, 2012
                    recognizes sexual harassment as a punishable offence;
                    ‘Sexual Harassment’ as per Section 11 of the POCSO Act, 2012
                    is any act of a person done/committed with a sexual intent
                    and such acts include –
                    <ol type="i">
                      <ListItem>
                        Uttering of any word or making sounds, or gestures or
                        exhibiting any object or part of body with the intention
                        that such word or sound shall be heard, or such gesture
                        or object or part of body shall be seen by the Child; or
                      </ListItem>
                      <ListItem>
                        making a Child exhibit his/her body or any part of
                        his/her body so as it is seen by such person or any
                        other person; or showing any object to a Child in any
                        form or media for pornographic purposes; or
                      </ListItem>
                      <ListItem>
                        repeatedly or constantly following or watching or
                        contacting a Child either directly or through
                        electronic, digital or any other means; or
                      </ListItem>
                      <ListItem>
                        threatening to use, in any form of media, a real or
                        fabricated depiction through electronic, film or digital
                        or any other mode, of any part of the body of the Child
                        or the involvement of the child in a sexual act; or
                      </ListItem>
                      <ListItem>
                        enticing a child for pornographic purposes or gives
                        gratification therefor.
                      </ListItem>
                    </ol>
                    Section 12 of the POCSO Act, 2012 states that an act of
                    sexual harassment upon a Child shall be punished with either
                    imprisonment for a term up to three years or fine or both.
                  </ListItem>
                  <ListItem>
                    <strong>Voyeurism and violation of privacy:</strong>
                    <ol type="i">
                      <ListItem>
                        Section 354C, the Indian Penal Code (IPC) 1860 states
                        that viewing and/or capturing the image of a girl or
                        woman going about her private acts, where she thinks
                        that no one is watching her, is a crime. The offender in
                        such cases of voyeurism can be punished with three to
                        seven years of imprisonment and a fine. While this
                        section of the IPC can only be used by girls and women,
                        the Information Technology Act, 2000 is gender neutral.
                      </ListItem>
                      <ListItem>
                        Section 66E, IT Act, 2000: Intentionally or knowingly
                        capturing, publishing or transmitting the image of a
                        private area of any person without his or her consent,
                        under circumstances violating the privacy of that
                        person, shall be punished with imprisonment which may
                        extend to three years or with fine not exceeding Rupees
                        two lakh or with both.
                      </ListItem>
                    </ol>
                  </ListItem>
                  <ListItem>
                    <strong>
                      Sending obscene material without the consent of the
                      recipient:
                    </strong>
                    <ol type="i">
                      <ListItem>
                        Section 67, IT Act, 2000: It punishes sharing obscene
                        material in electronic form. The punishment can be jail
                        for five years and a fine of Rs 10 lakhs.
                      </ListItem>
                      <ListItem>
                        Section 67A, IT Act, 2000: It punishes sharing material
                        containing sexually explicit act in electronic form with
                        jail for seven years and a fine of Rs 10 lakhs.
                      </ListItem>
                    </ol>
                  </ListItem>
                  The provisions of the Information Technology Act are not
                  gender specific and apply to everyone.
                </ol>
              </ListItem>
            </ol>
            <br />
            <br />
            <a name={tableData[4].id}>
              <StyledH3>{tableData[4].label}</StyledH3>
            </a>
            <Paragraph>
              While it is important that you do not commit or cause to commit an
              act prohibited under this Minor and Learner Interaction Guidelines
              and any applicable laws relating to Child Abuse, it is also very
              important that they do not remain bystanders when they encounter
              online abuse and exploitation. In fact, being aware of a child
              abuse case and not reporting it is illegal under the POCSO Act,
              2012.
            </Paragraph>
            <Paragraph>
              In the event the you suspects any form Child Abuse from your
              interaction with the Minor Learner, then you must write to
              <a href="mailto:legal@unacademy.com"> legal@unacademy.com</a>, so
              that the necessary actions can be taken by the Company.
            </Paragraph>
            <br />
            <br />
          </div>
        </Wrapper>
      </RightContainer>
    </>
  );
};

MinorPolicy.pageInfo = {
  name: 'Unacademy Minor Policy',
  isMWebSupported: true,
  layoutOptions: {
    hideGoalSelector: true,
    disableDarkMode: true
  }
};

export default MinorPolicy;
MinorPolicy.Layout = Layout;
MinorPolicy.Wrapper = PageReduxProvider;
